import React from 'react';
import ReactMarkdown from 'react-markdown';
import accountCircle from '../../Charts CL/imagesCl/account_circle.svg';
import accountCircleOne from '../../Charts CL/imagesCl/account_circle-one.svg';
import accountCircleTwo from '../../Charts CL/imagesCl/account_circle-two.svg';
import accountCircleThree from '../../Charts CL/imagesCl/account_circle-three.svg';
import accountCircleFour from '../../Charts CL/imagesCl/account_circle-four.svg';
import accountCircleFive from '../../Charts CL/imagesCl/account_circle-five.svg';
import IconCLChartButton from '../../Charts CL/imagesCl/IconCLChartButton.svg';
import barChartSvg from '../../Charts CL/imagesCl/bar_chart.svg';
import barChartSvgColor from '../../Charts CL/imagesCl/bar_chart_color.svg';
import {
  EDITABLE_PROFILE_FIELDS,
  FIELD_LABELS,
  isPlainTextProfile,
  parsePerfilAmpliado,
  updateLegacyProfileText,
  updateProfileField,
} from './profileUtils';
import './BuyerPersonaDetailEditor.css';

const getAccountIcon = (index, selected) => {
  if (selected) return accountCircle;
  if (index === 0) return accountCircleOne;
  if (index === 1) return accountCircleTwo;
  if (index === 2) return accountCircleThree;
  if (index === 3) return accountCircleFour;
  return accountCircleFive;
};

const getButtonClass = (index, clickState) => {
  const map = ['firstButtonGraphCl', 'secondButtonGraphCl', 'threeButtonGraphCl', 'fourButtonGraphCl', 'fiveButtonGraphCl', 'sixButtonGraphCl'];
  return clickState === index ? `ButtonsProfileGraphButtonCl ${map[index] || ''}` : 'ButtonsProfileGraphButtonCl';
};

const renderReadOnlyBody = (parsed) => {
  if (parsed.type === 'json') {
    const perfilData = parsed.data;
    return (
      <div className="perfil-descripcion">
        <p><strong>Perfil Clave:</strong> {perfilData.perfilClave}</p>
        <p><strong>Demografía Principal:</strong> {perfilData.demografiaPrincipal}</p>
        <p><strong>Intereses Principales:</strong> {perfilData.interesesPrincipales}</p>
        <p><strong>Vehículos:</strong> {perfilData.vehiculos}</p>
        <br />
        <p><strong>Descripción detallada</strong></p>
        <p><em>{perfilData.descripcionEjemplificada}</em></p>
        <br />
        <p><strong>Comportamiento de compra del segmento</strong></p>
        <p><em>{perfilData.comportamientoPropenso}</em></p>
      </div>
    );
  }

  if (parsed.type === 'legacyTitled') {
    return <ReactMarkdown>{parsed.data.body}</ReactMarkdown>;
  }

  return <ReactMarkdown>{parsed.data.raw}</ReactMarkdown>;
};

const renderEditableBody = (parsed, profileIndex, onChange) => {
  if (parsed.type === 'json') {
    const perfilData = parsed.data;
    return (
      <div className="perfil-descripcion buyerPersonaEditFields">
        {EDITABLE_PROFILE_FIELDS.filter((field) => field !== 'nombreCreativo').map((field) => (
          <label key={field} className="buyerPersonaEditField">
            <span className="buyerPersonaEditLabel">{FIELD_LABELS[field]}</span>
            {field === 'descripcionEjemplificada' || field === 'comportamientoPropenso' ? (
              <textarea
                className="buyerPersonaEditTextarea borde-inputs-generales"
                value={perfilData[field] || ''}
                onChange={(event) => onChange(profileIndex, field, event.target.value)}
                rows={4}
              />
            ) : (
              <input
                type="text"
                className="buyerPersonaEditInput borde-inputs-generales"
                value={perfilData[field] || ''}
                onChange={(event) => onChange(profileIndex, field, event.target.value)}
              />
            )}
          </label>
        ))}
      </div>
    );
  }

  if (parsed.type === 'plainText') {
    return (
      <textarea
        className="buyerPersonaEditTextarea buyerPersonaEditPlainTextTextarea borde-inputs-generales"
        value={parsed.data.raw || ''}
        onChange={(event) => onChange(profileIndex, 'raw', event.target.value)}
        rows={16}
      />
    );
  }

  const legacyValue = parsed.data.body ?? '';
  return (
    <textarea
      className="buyerPersonaEditTextarea borde-inputs-generales"
      value={legacyValue}
      onChange={(event) => onChange(profileIndex, 'body', event.target.value)}
      rows={10}
    />
  );
};

const renderPlainTextTitle = () => (
  <p className="StrongTitleGraphCl buyerPersonaEditFieldTitle">
    <strong className="StrongTxtTitleGraphCl">¿Cómo es éste perfil?</strong>
  </p>
);

const renderEditProfileContent = (parsed, profileIndex, onChange) => {
  if (isPlainTextProfile(parsed)) {
    return (
      <div className="buyerPersonaEditPlainText">
        {renderPlainTextTitle()}
        {renderEditableBody(parsed, profileIndex, onChange)}
      </div>
    );
  }

  if (parsed.type === 'json') {
    return (
      <>
        <label className="buyerPersonaEditField buyerPersonaEditFieldTitle">
          <span className="buyerPersonaEditLabel StrongTitleGraphCl">
            <strong className="StrongTxtTitleGraphCl">¿Cómo es éste perfil?</strong>
          </span>
          <input
            type="text"
            className="buyerPersonaEditInput borde-inputs-generales"
            value={parsed.data.nombreCreativo || ''}
            onChange={(event) => onChange(profileIndex, 'nombreCreativo', event.target.value)}
          />
        </label>
        {renderEditableBody(parsed, profileIndex, onChange)}
      </>
    );
  }

  return (
    <>
      <label className="buyerPersonaEditField buyerPersonaEditFieldTitle">
        <span className="buyerPersonaEditLabel StrongTitleGraphCl">
          <strong className="StrongTxtTitleGraphCl">¿Cómo es éste perfil?</strong>
        </span>
        <input
          type="text"
          className="buyerPersonaEditInput borde-inputs-generales"
          value={parsed.data.title || ''}
          onChange={(event) => onChange(profileIndex, 'title', event.target.value)}
        />
      </label>
      {renderEditableBody(parsed, profileIndex, onChange)}
    </>
  );
};

const BuyerPersonaDetailEditor = ({
  profiles,
  profileWeights,
  clickState,
  onSelectProfile,
  editMode,
  onChange,
  countryFormat,
  organizationName,
}) => {
  if (!profiles || profiles.length === 0) return null;

  const handleFieldChange = (index, field, value) => {
    if (field === 'raw') {
      onChange(updateLegacyProfileText(profiles, index, value));
      return;
    }
    onChange(updateProfileField(profiles, index, field, value));
  };

  if (countryFormat === 'mx') {
    return (
      <div className="buyerPersonaDetailMx">
        <div className="buyerPersonText">
          <h2>DETALLE DE Buyer Person</h2>
          <p>
            Una vez completado el perfil de clientes de{' '}
            <strong>{organizationName}</strong>, se obtienen los Buyer Person correspondientes.
          </p>
        </div>
        {profiles.map((item, index) => {
          const parsed = parsePerfilAmpliado(item.perfil_ampliado);
          return (
            <div key={`${item.Perfil}-${index}`} className="finalGraphText">
              <p>
                <strong>{item.Perfil}</strong>
              </p>
              {editMode ? (
                renderEditProfileContent(parsed, index, handleFieldChange)
              ) : (
                <p>{item.perfil_ampliado}</p>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  const selectedProfile = profiles[clickState];
  const selectedParsed = selectedProfile ? parsePerfilAmpliado(selectedProfile.perfil_ampliado) : null;
  const perfilNuevo = selectedParsed?.type === 'json'
    ? selectedParsed.data.nombreCreativo
    : selectedParsed?.type === 'legacyTitled'
      ? selectedParsed.data.title
      : '';

  return (
    <div className="finalGraph">
      <div className="finalGraphContenedor">
        <div className="buyerPersonTextCl">
          <h2>Detalle de Buyer persona</h2>
          <p>
            A continuación, te presentamos los diferentes <strong>buyer personas</strong> encontrados a partir del análisis de los clientes de
            <strong> {organizationName}</strong>.
          </p>
          <p>¡Llegó el momento de conocer a tus clientes!</p>
        </div>

        {profileWeights.length > 0 && (
          <div className="ProfileGraphText">
            <div className="ProfileGraphTextDivContainer">
              <div className="ButtonsProfileGraphTextCl">
                {profiles.map((item, index) => (
                  <div
                    key={index}
                    onClick={() => onSelectProfile(index)}
                    className={getButtonClass(index, clickState)}
                  >
                    <div className="ButtonsProfileGraphButtonClContainer">
                      <div style={{ display: 'flex', marginBottom: '0.5em' }}>
                        <img src={getAccountIcon(index, clickState === index)} alt="" />
                        <p><strong>Buyer persona {index + 1}</strong></p>
                      </div>
                      <div style={{ display: 'flex', alignContent: 'center' }}>
                        <img src={clickState !== index ? barChartSvgColor : barChartSvg} alt="" />
                        <p><strong>{profileWeights[index]}%</strong> tiene este perfil</p>
                      </div>
                    </div>
                    <img src={IconCLChartButton} alt="" className="icon-cl-chart-button" />
                  </div>
                ))}
              </div>

              {selectedProfile && (
                <div key={selectedProfile.Perfil + 'finalGraphText'} className="finalGraphText">
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <div className="finalGraphTextTxtImg">
                        <img src={getAccountIcon(clickState, false)} alt="" />
                        <p className="finalGraphTextTxtImgBp">
                          <strong>Buyer Persona {clickState + 1}</strong>
                        </p>
                      </div>
                      <div className="finalGraphTextTxtImg">
                        <img src={barChartSvgColor} alt="" style={{ marginRight: '0.3em' }} />
                        <p><strong>{profileWeights[clickState]}%</strong> tiene este perfil</p>
                      </div>
                    </div>

                    {editMode ? (
                      renderEditProfileContent(selectedParsed, clickState, handleFieldChange)
                    ) : (
                      <>
                        {!isPlainTextProfile(selectedParsed) && (
                          <p className="StrongTitleGraphCl" style={{ marginTop: '1em', marginBottom: '0.5em' }}>
                            <strong className="StrongTxtTitleGraphCl">¿Cómo es éste perfil?</strong>
                            {' ' + perfilNuevo}
                          </p>
                        )}
                        {selectedParsed && (
                          <>
                            {isPlainTextProfile(selectedParsed) && renderPlainTextTitle()}
                            {renderReadOnlyBody(selectedParsed)}
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BuyerPersonaDetailEditor;
